import React, { ComponentType, createContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { GetJsDataInfo } from '@bingads-webui-clientcenter/js-data-cc-rest-base';
import type { CcJsData, CcJsDataContextProviderProps } from './types';

export const CcJsDataContext = createContext<CcJsData | null>(null);
export const JsDataErrorPolicyContext = createContext<((error: unknown) => boolean) | null>(null);

export const CcJsDataContextConsumer = CcJsDataContext.Consumer;

export const withCcJsData = <TProps extends { ccJsData: object }>(
  WrappedComponent: ComponentType<TProps>
): ComponentType<Omit<TProps, 'ccJsData'>> => {
  const HoC = (props: Omit<TProps, 'ccJsData'>) => (
    <CcJsDataContext.Consumer>
      {ccJsData => <WrappedComponent {...props as TProps} ccJsData={ccJsData as TProps['ccJsData']} />}
    </CcJsDataContext.Consumer>
  );
  HoC.displayName = `withCcJsData(${getDisplayName(WrappedComponent)})`;

  hoistNonReactStatics(HoC, WrappedComponent);

  return HoC as ComponentType<Omit<TProps, 'ccJsData'>>;
};

export const CcJsDataContextProvider = ({
  customerId,
  userId,
  appendErrorSummary,
  handleAjaxAuthFailure,
  clientCenterBaseUrl,
  initialJsData,
  children,
  resourceNames
}: CcJsDataContextProviderProps) => {
  const getJsData = () => {
    if (initialJsData) {
      return initialJsData;
    }

    const options = {
      cidFetcher: () => customerId,
      uidFetcher: () => userId,
      appendErrorSummary,
      handleAjaxAuthFailure,
      willSupportCors: true,
    };

    return GetJsDataInfo(options, clientCenterBaseUrl, resourceNames).jsDataCcRest;
  };

  const jsData = useMemo(getJsData, [customerId, userId, clientCenterBaseUrl, initialJsData, resourceNames]);

  return (
    <CcJsDataContext.Provider value={jsData}>
      {children}
    </CcJsDataContext.Provider>
  );
};

CcJsDataContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  customerId: PropTypes.number,
  userId: PropTypes.number,
  clientCenterBaseUrl: PropTypes.string,
  appendErrorSummary: PropTypes.func,
  handleAjaxAuthFailure: PropTypes.func,
  initialJsData: PropTypes.shape({}),
  resourceNames: PropTypes.arrayOf(PropTypes.string),
};

CcJsDataContextProvider.defaultProps = {
  customerId: null,
  userId: null,
  clientCenterBaseUrl: null,
  appendErrorSummary: _.noop,
  handleAjaxAuthFailure: _.noop,
  initialJsData: null,
  resourceNames: [],
};
